import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from "@angular/router";
import { LoginService } from "../../services/auth/login/login.service";
import { Observable, of, skip } from "rxjs";
import { switchMap, filter, map, take } from "rxjs/operators";
import { DEFAULT_AFTER_LOGIN } from "app/core/config/app.config";
import { AccountService } from "../../services/account/account.service";
import { UserProfileService } from "../../services/user-profile/user-profile.service";

@Injectable({
  providedIn: "root"
})
export class AuthAccountGuard
  implements CanActivate, CanActivateChild, CanLoad
{
  /**
   * Constructor
   */
  constructor(
    private loginService: LoginService,
    private _router: Router,
    private accountService: AccountService,
    private userProfileService: UserProfileService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Can activate
   *
   * @param route
   * @param state
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    const redirectUrl = state.url === "/sign-out" ? "/" : state.url;
    return this._check(redirectUrl, state);
  }

  /**
   * Can activate child
   *
   * @param childRoute
   * @param state
   */
  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const redirectUrl = state.url === "/sign-out" ? "/" : state.url;
    return this._check(redirectUrl, state);
  }

  /**
   * Can load
   *
   * @param route
   * @param segments
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this._check("/");
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check the authenticated status
   *
   * @private
   */
  private _check(
    redirectURL: string,
    state?: RouterStateSnapshot
  ): Observable<boolean> {
    // Check the authentication status
    return this.loginService.isAuthenticated().pipe(
      switchMap((authenticated) => {
        console.log(authenticated);
        // If the user is not authenticated...
        if (!authenticated.isAuth) {
          this._router.navigate(["/auth/login"]);
          // Prevent the access
          return of(false);
        } else if (!authenticated.emailVerified) {
          this._router.navigate(["/auth/verify-email"]);
          // Prevent the access
          return of(false);
        } else if (!authenticated.mobileVerified) {
          this._router.navigate(["/auth/verify-mobile"]);
          // Prevent the access
          return of(false);
          // } else if (
          //   authenticated.profileVerified === 0 ||
          //   authenticated.profileVerified === 1
          // ) {
          //   this._router.navigate(["/auth/id-check"]);
          //   // Prevent the access
          //   return of(false);
        } else if (authenticated.accountCount === 0) {
          if (
            state.url === "/welcome" ||
            state.url === "/business-details" ||
            state.url === "/authority-verification" ||
            state.url === DEFAULT_AFTER_LOGIN
          ) {
            return of(true);
          }
          // Prevent the access
          return of(false);
        }
        // Comment for testing with existing account
        else if (authenticated.accountCount > 0 && state?.url === "/business-details") {
          this._router.navigate([DEFAULT_AFTER_LOGIN]);
          return of(false);
        }
        
        // Check account verification status
        if (authenticated.accountCount > 0) {
          return this.accountService.currentAccount$.pipe(
            filter((account) => !!account && Object.keys(account).length > 0),
            take(1),
            switchMap((currentAccount: any): Observable<boolean> => {
              const verificationStatus = currentAccount?.verification?.status;
              
              if (state?.url === "/authority-verification") {
                if (verificationStatus === "accepted") {
                  this._router.navigate([DEFAULT_AFTER_LOGIN]);
                  return of(false);
                }
                return of(true);
              }
              
              if (verificationStatus !== "accepted") {
                this._router.navigate(["/authority-verification"]);
                return of(false);
              }

              // TEZY2-4394 — a deactivated member keeps a valid session token, so
              // block their access. Only send them to the suspended screen when
              // they have no other account they can still use; if they are active
              // in another account, account switching moves them there instead.
              // Active members (or those still active elsewhere) must not sit on
              // the suspended screen.
              const activeUser = this.userProfileService.currentUser.getValue();
              const isDeactivated = this.accountService.isMemberDeactivated(
                currentAccount,
                activeUser?.id
              );
              const suspendedEverywhere =
                isDeactivated &&
                !this.accountService.hasOtherActiveAccount(
                  currentAccount?.uuid,
                  activeUser?.id
                );
              if (suspendedEverywhere) {
                if (state?.url === "/account-suspended") {
                  return of(true);
                }
                this._router.navigate(["/account-suspended"]);
                return of(false);
              }
              if (state?.url === "/account-suspended") {
                this._router.navigate([DEFAULT_AFTER_LOGIN]);
                return of(false);
              }

              if (state?.url === "/welcome") {
                const currentUser = this.userProfileService.currentUser.getValue();
                if (currentUser && currentAccount?.members) {
                  const currentUserMember = currentAccount.members.find(
                    (m: any) => m.user_id === currentUser.id
                  );
                  // if user is Resource, allow them to stay on /welcome
                  if (currentUserMember?.role === 0) {
                    return of(true);
                  }
                }
                this._router.navigate([DEFAULT_AFTER_LOGIN]);
                return of(false);
              }
              
              return of(true);
            })
          );
        }
        
        return of(true);
      })
    );
  }
}
